import { useDispatch, useSelector } from "react-redux";
import {
  Calendar, GlobalModalComponentName, Leader, LeaderCalendarUpdate, RootState, SharedLeaderGrant, ThunkDispatchType,
  actions
} from "../../../store";
import { appCore } from "../../../store/cabinetApi/appCore";
import SupportMap from "./SupportMap";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import CabinetPage from "../../../components/Common/CabinetPage";
import { Box, Typography } from "@mui/material";
import { useNavigate } from "react-router";
import { NEW_TIER_IDS, PAGE_URL } from "../../../constants";

const SupportMapContainer = ({ embedded = false }: { embedded?: boolean }) => {
  const dispatch = useDispatch<ThunkDispatchType>();
  const leaders = useSelector((root: RootState) => root.leaders.orgLeaders);
  const leadersLoaded = useSelector((root: RootState) => root.leaders.orgLeadersLoaded);
  const leaderCategories = useSelector((root: RootState) => root.leaders.orgLeaderCategories);
  const calendars = useSelector((root: RootState) => root.schedule.calendars);
  const user = useSelector((root: RootState) => root.auth.user);
  const organization = useSelector((root: RootState) => root.organization);
  const globalModalOpen = useSelector((root: RootState) => root.globalModal.open);
  const navigate = useNavigate();
  const [openedAccessModal, setOpenedAccessModal] = useState(false);
  const previousGlobalModalOpen = useRef(globalModalOpen);

  const { data: orgSharedLeaderGrantsList = [] } = appCore.useAppCoreOrgSharedLeadersListQuery();
  const sharedLeaderGrants = useMemo((): RootState["leaders"]["orgSharedLeaderGrants"] => {
    const byLeader: Record<number, typeof orgSharedLeaderGrantsList> = {};
    orgSharedLeaderGrantsList.forEach((grant) => {
      if (grant.leader in byLeader) {
        byLeader[grant.leader].push(grant);
      } else {
        byLeader[grant.leader] = [grant];
      }
    });
    return byLeader as unknown as RootState["leaders"]["orgSharedLeaderGrants"];
  }, [orgSharedLeaderGrantsList]);

  const [revokeOrgSharedLeader] = appCore.useAppCoreOrgSharedLeadersRevokePartialUpdateMutation();
  const revokeGrant = useCallback(
    async (grant: SharedLeaderGrant) => {
      await revokeOrgSharedLeader({
        id: grant.id,
        patchedSharedLeaderGrant: {},
      }).unwrap();
    },
    [revokeOrgSharedLeader]
  );

  const createLeader = (leader: Leader, leaderPic: File | null, idx?: number | undefined) => (
    dispatch(actions.leaders.createAdminLeader(leader, leaderPic, idx))
  );
  const deleteLeader = (leaderId: number) => dispatch(actions.leaders.deleteAdminLeader(leaderId));

  useEffect(() => {
    dispatch(actions.leaders.fetchOrganizationLeader());
    dispatch(actions.leaders.fetchOrganizationLeaderProfileCategories());
    dispatch(actions.organization.fetchOrgUserAutocompleteOptions());
    dispatch(actions.schedule.fetchRemoteCalendars());
  }, [dispatch]);

  const hasStrategicSupportMap = [
    NEW_TIER_IDS.STRATEGIC,
    NEW_TIER_IDS.ENTERPRISE,
  ].includes(user?.active_license.license_tier || NEW_TIER_IDS.BASIC);
  const hasSupportMapAccess = hasStrategicSupportMap || !!user?.features?.SUPPORT_MAP;
  const shouldBlockSupportMap = !embedded && !hasSupportMapAccess && !!user;

  useEffect(() => {
    if (shouldBlockSupportMap && !openedAccessModal) {
      dispatch(actions.globalModal.openModal(GlobalModalComponentName.CABINET_PROMO));
      setOpenedAccessModal(true);
    }
  }, [dispatch, openedAccessModal, shouldBlockSupportMap]);

  useEffect(() => {
    const modalJustClosed = previousGlobalModalOpen.current && !globalModalOpen;
    previousGlobalModalOpen.current = globalModalOpen;

    if (shouldBlockSupportMap && openedAccessModal && modalJustClosed) {
      if ((window.history.state?.idx || 0) > 0) {
        navigate(-1);
      } else {
        navigate(PAGE_URL.DASHBOARD, { replace: true });
      }
    }
  }, [globalModalOpen, navigate, openedAccessModal, shouldBlockSupportMap]);

  const handleCreateLeader = async (
    leader: Leader, leaderPic: File | null, idx?: number, cals?: number[]) => {
    const newLeader = await createLeader(leader, leaderPic, idx);
    if (newLeader && cals?.length) {
      const providerLookup = calendars.reduce<Record<number, number>>((lookup, calendar) => ({
        ...lookup,
        [calendar.id]: calendar.provider,
      }), {});
      const leaderToCalendars: LeaderCalendarUpdate[] = cals
        .filter(cal => providerLookup[cal])
        .map(cal => ({
          leader_id: newLeader.id,
          provider: providerLookup[cal],
          calId: cal,
          associate: true,
          prevent_conflict: true,
          allow_calendar_analytics: true,
          allow_crm: true,
        }));
      if (leaderToCalendars.length) {
        await dispatch(actions.schedule.updateLeaderCalendars(leaderToCalendars));
      }
    }
    dispatch(actions.leaders.fetchLeaders());
  };

  const handleGetEmailCalendars = async (email: string): Promise<Calendar[]> => {
    const emailCalendars = await dispatch(actions.schedule.addDelegateCalendarsForEmails([email]));
    return emailCalendars[email] || [];
  };

  const content = shouldBlockSupportMap ? null : (
    <SupportMap
      user={user}
      leaders={leaders}
      loaded={leadersLoaded}
      sharedLeaderGrants={sharedLeaderGrants}
      leaderCategories={leaderCategories}
      revokeGrant={revokeGrant}
      onCreateLeader={handleCreateLeader}
      shareWithUserOptions={organization.userAutocompleteOptions}
      onDeleteLeader={deleteLeader}
      hasEditLeaderPermission={!!user?.permissions.WRITE_LEADER_DATA_ACCESS}
      calendars={calendars}
      onGetEmailCalendars={handleGetEmailCalendars}
    />
  );

  if (embedded) return content;

  return (
    <CabinetPage
      pageName="Support Map"
      headerContent={(
        <Typography variant="h1">
          Support Map
        </Typography>
      )}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          padding: 5,
          paddingTop: 4
        }}>
        {content}
      </Box>
    </CabinetPage>
  );
};

export default SupportMapContainer;
